// Shop screen - buy/sell weapons and armor

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam, isItemDiscounted } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import {
  Item, ITEM_TYPE, WEAPON_SUBTYPE, SUBTYPE_NAMES,
  SPEARS, SWORDS, AXES, MACES, BOWS, CROSSBOWS, ARMORS,
  getSellPrice
} from '../data/items';
import { getMeleeWeapon, getRangedWeapon, getArmor } from '../entities/Gladiator';
import { ScrollableList } from '../components/ScrollableList';

type ShopCategory = 'spears' | 'swords' | 'axes' | 'maces' | 'bows' | 'crossbows' | 'armors';

export class Shop implements Screen {
  readonly state = ScreenState.SHOP;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedCategory: ShopCategory = 'spears';
  private selectedGladiatorIndex: number = 0;
  private itemList: ScrollableList<Item>;

  constructor(context: ScreenContext) {
    this.context = context;
    this.itemList = new ScrollableList<Item>({
      x: 20,
      y: 90,
      width: 350,
      height: 320,
      itemHeight: 30,
      headerHeight: 0,
      style: {
        backgroundColor: '#ffffff',
        borderColor: CONSTANTS.TABLE_BORDER,
        headerColor: '#000000',
        selectedColor: CONSTANTS.SELECTED_GLADIATOR,
        scrollButtonColor: '#0000aa',
        scrollButtonDisabledColor: '#aaaaaa',
        scrollButtonTextColor: '#ffffff',
        positionTextColor: '#666666',
      },
      renderItem: (canvas, item, x, y, width, _height, _isSelected) => {
        const discounted = this.isDiscounted(item);
        const nameColor = discounted ? '#006600' : '#000000';
        canvas.drawText(item.name, x + 10, y + 8, CONSTANTS.MAIN_FONT, nameColor);

        if (discounted) {
          canvas.drawText('ALE', x + 10 + canvas.measureText(item.name, CONSTANTS.MAIN_FONT).width + 5,
            y + 8, CONSTANTS.MSG_FONT, '#ff0000');
        }

        const currentPrice = discounted ? Math.round(item.price * GAME_CONSTANTS.DISCOUNT_PRICE) : item.price;
        if (discounted) {
          canvas.drawText(`${item.price}`, x + width - 95, y + 8, CONSTANTS.MSG_FONT, '#999999');
          canvas.drawText(`→ ${currentPrice} kr`, x + width - 55, y + 8, CONSTANTS.MSG_FONT, '#006600');
        } else {
          canvas.drawText(`${currentPrice} kr`, x + width - 55, y + 8, CONSTANTS.MSG_FONT, '#000066');
        }
      },
    });
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
    // Reset gladiator selection when state is set
    this.selectedGladiatorIndex = 0;
  }

  enter(): void {
    console.log('Entering Shop');
    this.selectedCategory = 'spears';
    this.selectedGladiatorIndex = 0;
    this.updateItemList();
  }

  private updateItemList(): void {
    const items = this.getItemsForCategory();
    this.itemList.setItems(items.map((item, idx) => ({ id: `item-${idx}`, data: item })));
    this.itemList.reset();
  }

  exit(): void {
    console.log('Exiting Shop');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      return;
    }

    const team = getCurrentTeam(this.gameState);

    canvas.clear(CONSTANTS.MAIN_BACKGROUND);

    // Header
    this.renderHeader(canvas, team);

    // Category tabs
    this.renderCategoryTabs(canvas);

    // Item list (using ScrollableList component)
    this.itemList.render(canvas);

    // Selected item details
    this.renderItemDetails(canvas);

    // Current gladiator equipment (with arrows for navigation)
    this.renderGladiatorEquipment(canvas, null);

    // Back button
    this.renderBackButton(canvas);
  }

  private renderHeader(canvas: Canvas, team: any): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 45, CONSTANTS.TOP1_BACKGROUND);

    canvas.drawText('Asekauppa', 20, 10, CONSTANTS.BIG_FONT, '#000000');

    if (team) {
      canvas.drawText(
        `${GAME_CONSTANTS.MONEY_PREFIX}: ${team.money} ${GAME_CONSTANTS.MONEY_SUFFIX}`,
        CONSTANTS.CANVAS_WIDTH - 20,
        10,
        CONSTANTS.BIG_FONT,
        '#000000',
        'right'
      );
    }
  }

  private renderCategoryTabs(canvas: Canvas): void {
    const categories: { key: ShopCategory; label: string }[] = [
      { key: 'spears', label: 'Keihäät' },
      { key: 'swords', label: 'Miekat' },
      { key: 'axes', label: 'Kirveet' },
      { key: 'maces', label: 'Nuijat' },
      { key: 'bows', label: 'Jouset' },
      { key: 'crossbows', label: 'Varsijouset' },
      { key: 'armors', label: 'Haarniskat' },
    ];

    const tabY = 50;
    const tabWidth = 100;
    const tabHeight = 28;
    const startX = 20;

    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i];
      const tabX = startX + i * (tabWidth + 5);
      const isSelected = this.selectedCategory === cat.key;

      const bgColor = isSelected ? '#0000aa' : '#666666';
      canvas.drawRect(tabX, tabY, tabWidth, tabHeight, bgColor);
      canvas.drawText(cat.label, tabX + tabWidth / 2, tabY + 7, CONSTANTS.MAIN_FONT, '#ffffff', 'center');

      canvas.addClickableRegion({
        id: `tab-${cat.key}`,
        x: tabX,
        y: tabY,
        width: tabWidth,
        height: tabHeight,
        onClick: () => {
          this.selectedCategory = cat.key;
          this.updateItemList();
        },
      });
    }
  }

  private getItemsForCategory(): Item[] {
    switch (this.selectedCategory) {
      case 'spears': return SPEARS;
      case 'swords': return SWORDS;
      case 'axes': return AXES;
      case 'maces': return MACES;
      case 'bows': return BOWS;
      case 'crossbows': return CROSSBOWS;
      case 'armors': return ARMORS;
    }
  }

  private isDiscounted(item: Item): boolean {
    return this.gameState ? isItemDiscounted(this.gameState, item.id) : false;
  }

  private renderItemDetails(canvas: Canvas): void {
    const detailX = 390;
    const detailY = 90;
    const detailWidth = 385;
    const detailHeight = 200;

    canvas.drawRect(detailX, detailY, detailWidth, detailHeight, '#ffffff');
    canvas.drawRectOutline(detailX, detailY, detailWidth, detailHeight, CONSTANTS.TABLE_BORDER);

    const item = this.itemList.getSelectedItem();
    if (!item) return;

    // Item name
    canvas.drawText(item.name, detailX + 15, detailY + 10, CONSTANTS.GLADIATOR_NAME_FONT, '#000066');

    // Item type
    const typeName = item.type === ITEM_TYPE.ARMOUR ? 'Haarniska' : SUBTYPE_NAMES[item.subtype!];
    canvas.drawText(typeName, detailX + 15, detailY + 35, CONSTANTS.MAIN_FONT, '#666666');

    // Stats
    const statsY = detailY + 60;
    const lineHeight = 22;

    if (item.type === ITEM_TYPE.MELEE_WEAPON) {
      canvas.drawText(`Vahinko: ${item.dmg}`, detailX + 15, statsY, CONSTANTS.MAIN_FONT, '#000000');
      canvas.drawText(`Osuma-bonus: +${item.hitBonus ?? 0}`, detailX + 15, statsY + lineHeight, CONSTANTS.MAIN_FONT, '#000000');
      canvas.drawText(`Torjunta-bonus: +${item.blockBonus ?? 0}`, detailX + 15, statsY + lineHeight * 2, CONSTANTS.MAIN_FONT, '#000000');
    } else if (item.type === ITEM_TYPE.RANGED_WEAPON) {
      canvas.drawText(`Vahinko: ${item.dmg}`, detailX + 15, statsY, CONSTANTS.MAIN_FONT, '#000000');
      if (item.subtype === WEAPON_SUBTYPE.CROSSBOW) {
        canvas.drawText('Vaatii lataamisen iskun jälkeen', detailX + 15, statsY + lineHeight, CONSTANTS.MSG_FONT, '#666666');
      }
    } else if (item.type === ITEM_TYPE.ARMOUR) {
      canvas.drawText(`Suoja: ${item.protection}`, detailX + 15, statsY, CONSTANTS.MAIN_FONT, '#000000');
    }

    // Prices
    const priceY = statsY + lineHeight * 4;
    const discounted = this.isDiscounted(item);
    const currentPrice = discounted ? Math.round(item.price * GAME_CONSTANTS.DISCOUNT_PRICE) : item.price;
    const sellPrice = getSellPrice(item);

    if (discounted) {
      // Show original price crossed out and new price
      canvas.drawText(`Norm. hinta: ${item.price} kr`, detailX + 15, priceY, CONSTANTS.MSG_FONT, '#999999');
      canvas.drawText(`ALE-hinta: ${currentPrice} kr`, detailX + 15, priceY + lineHeight, CONSTANTS.MAIN_FONT, '#006600');
      canvas.drawText(`(-${Math.round((1 - currentPrice / item.price) * 100)}%)`, detailX + 150, priceY + lineHeight, CONSTANTS.MSG_FONT, '#ff0000');
      canvas.drawText(`Myyntihinta: ${sellPrice} kr`, detailX + 15, priceY + lineHeight * 2, CONSTANTS.MSG_FONT, '#660000');
    } else {
      canvas.drawText(`Hinta: ${currentPrice} kr`, detailX + 15, priceY, CONSTANTS.MAIN_FONT, '#000066');
      canvas.drawText(`Myyntihinta: ${sellPrice} kr`, detailX + 15, priceY + lineHeight, CONSTANTS.MSG_FONT, '#660000');
    }

    // Buy button
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();

    if (team && gladiator && team.money >= currentPrice) {
      canvas.drawButton(
        'buy-item',
        'Osta',
        detailX + detailWidth - 100,
        detailY + detailHeight - 45,
        80,
        30,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        '#006600',
        () => this.buyItem(item)
      );
    }
  }

  private renderGladiatorEquipment(canvas: Canvas, _gladiator: any): void {
    const equipX = 390;
    const equipY = 300;
    const equipWidth = 385;
    const equipHeight = 130; // Increased height for arrows

    canvas.drawRect(equipX, equipY, equipWidth, equipHeight, '#e8e8e8');
    canvas.drawRectOutline(equipX, equipY, equipWidth, equipHeight, CONSTANTS.TABLE_BORDER);

    // Get gladiator from local selection
    const team = getCurrentTeam(this.gameState!);
    if (!team || team.gladiatorIds.length === 0) {
      canvas.drawText('Valitse gladiaattori varusteluun', equipX + equipWidth / 2, equipY + equipHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    // Ensure selection is valid
    if (this.selectedGladiatorIndex >= team.gladiatorIds.length) {
      this.selectedGladiatorIndex = 0;
    }

    const gladiatorId = team.gladiatorIds[this.selectedGladiatorIndex];
    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator) return;

    // Navigation arrows
    const arrowY = equipY + 25;
    const arrowWidth = 30;
    const arrowHeight = 30;

    // Previous arrow
    const canGoPrev = this.selectedGladiatorIndex > 0;
    canvas.drawButton(
      'prev-gladiator',
      '◀',
      equipX + 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoPrev ? '#006600' : '#444444',
      () => {
        if (canGoPrev) {
          this.selectedGladiatorIndex--;
        }
      }
    );

    // Portrait and info
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, equipX + 50, equipY + 10, 48, 48);
    }
    canvas.drawText(gladiator.name, equipX + 110, equipY + 15, CONSTANTS.GLADIATOR_NAME_FONT, CONSTANTS.GLADIATOR_TITLE);
    canvas.drawText(gladiator.race.name, equipX + 110, equipY + 38, CONSTANTS.MSG_FONT, '#666666');

    // Next arrow
    const canGoNext = this.selectedGladiatorIndex < team.gladiatorIds.length - 1;
    canvas.drawButton(
      'next-gladiator',
      '▶',
      equipX + equipWidth - arrowWidth - 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoNext ? '#006600' : '#444444',
      () => {
        if (canGoNext) {
          this.selectedGladiatorIndex++;
        }
      }
    );

    // Gladiator counter
    canvas.drawText(
      `${this.selectedGladiatorIndex + 1} / ${team.gladiatorIds.length}`,
      equipX + equipWidth / 2,
      equipY + 52,
      CONSTANTS.MSG_FONT,
      '#666666',
      'center'
    );

    // Current equipment
    const meleeWeapon = getMeleeWeapon(gladiator);
    const rangedWeapon = getRangedWeapon(gladiator);
    const armor = getArmor(gladiator);

    const lineY = equipY + 75;
    const lineHeight = 18;

    // Melee slot
    canvas.drawText(`Lähitaistelu: ${meleeWeapon?.name ?? '-'}`, equipX + 15, lineY, CONSTANTS.MSG_FONT, '#000000');
    if (meleeWeapon) {
      canvas.drawButton('sell-melee', 'Myy', equipX + 200, lineY - 5, 45, 20, CONSTANTS.MSG_FONT, '#ffffff', '#aa0000',
        () => this.sellItem(0));
    }

    // Ranged slot
    canvas.drawText(`Etätaistelu: ${rangedWeapon?.name ?? '-'}`, equipX + 15, lineY + lineHeight, CONSTANTS.MSG_FONT, '#000000');
    if (rangedWeapon) {
      canvas.drawButton('sell-ranged', 'Myy', equipX + 200, lineY + lineHeight - 5, 45, 20, CONSTANTS.MSG_FONT, '#ffffff', '#aa0000',
        () => this.sellItem(1));
    }

    // Armor slot
    canvas.drawText(`Haarniska: ${armor?.name ?? '-'}`, equipX + 260, lineY, CONSTANTS.MSG_FONT, '#000000');
    if (armor) {
      canvas.drawButton('sell-armor', 'Myy', equipX + 340, lineY - 5, 45, 20, CONSTANTS.MSG_FONT, '#ffffff', '#aa0000',
        () => this.sellItem(2));
    }
  }

  private getSelectedGladiator(): any {
    if (!this.gameState) return null;
    const team = getCurrentTeam(this.gameState);
    if (!team || team.gladiatorIds.length === 0) return null;

    if (this.selectedGladiatorIndex >= team.gladiatorIds.length) {
      this.selectedGladiatorIndex = 0;
    }

    const gladiatorId = team.gladiatorIds[this.selectedGladiatorIndex];
    return this.gameState.gladiators.get(gladiatorId);
  }

  private renderBackButton(canvas: Canvas): void {
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      CONSTANTS.CANVAS_HEIGHT - 50,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      CONSTANTS.BUTTON_AREA_BACKGROUND,
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );
  }

  private buyItem(item: Item): void {
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();
    if (!team || !gladiator) return;

    const discounted = this.isDiscounted(item);
    const price = discounted ? Math.round(item.price * GAME_CONSTANTS.DISCOUNT_PRICE) : item.price;

    // Determine slot
    let slot: number;
    if (item.type === ITEM_TYPE.MELEE_WEAPON) {
      slot = GAME_CONSTANTS.MELEE_WEAPON_SLOT;
    } else if (item.type === ITEM_TYPE.RANGED_WEAPON) {
      slot = GAME_CONSTANTS.RANGED_WEAPON_SLOT;
    } else {
      slot = GAME_CONSTANTS.ARMOUR_SLOT;
    }

    // Refund existing item
    const existingItem = gladiator.items[slot];
    let refund = 0;
    if (existingItem) {
      refund = getSellPrice(existingItem);
    }

    const totalCost = price - refund;
    if (team.money < totalCost) return;

    // Execute purchase
    team.money -= totalCost;
    gladiator.items[slot] = item;

    console.log(`Bought ${item.name} for ${price} kr (refund: ${refund} kr)`);
  }

  private sellItem(slot: number): void {
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();
    if (!team || !gladiator) return;

    const item = gladiator.items[slot];
    if (!item) return;

    const sellPrice = getSellPrice(item);
    team.money += sellPrice;
    gladiator.items[slot] = null;

    console.log(`Sold ${item.name} for ${sellPrice} kr`);
  }

  handleClick(x: number, y: number): void {
    console.log(`Shop click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else {
      this.itemList.handleKeyDown(key);
    }
  }

  handleWheel(x: number, y: number, deltaY: number): void {
    this.itemList.handleWheel(x, y, deltaY);
  }
}
